import React, { useCallback, useEffect, useRef, useState } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import LereService from '~/services/LereService';
import { formatDateLongMonthWithMeridiemShort } from '~/services/TimeService';

import { HurdleHeatmap, HurdleHeatmapCatalog, LereTabComponentProps } from './types';

const SEARCH_DEBOUNCE_MS = 400;
const PDF_REFRESH_BUFFER_MS = 1_000;
const EMPTY_CATALOG: HurdleHeatmapCatalog = { offerIds: [], count: 0 };

function isNotFoundError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === 404;
}

export default function HurdleHeatmaps({ onLereError }: LereTabComponentProps) {
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [catalog, setCatalog] = useState<HurdleHeatmapCatalog>(EMPTY_CATALOG);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [catalogFailed, setCatalogFailed] = useState(false);
  const [catalogRequestNumber, setCatalogRequestNumber] = useState(0);
  const [selectedOfferId, setSelectedOfferId] = useState<string>();
  const [heatmap, setHeatmap] = useState<HurdleHeatmap>();
  const [isHeatmapLoading, setIsHeatmapLoading] = useState(false);
  const [heatmapNotFound, setHeatmapNotFound] = useState(false);
  const latestCatalogRequest = useRef(0);
  const latestHeatmapRequest = useRef(0);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearchQuery(searchValue.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    const requestNumber = latestCatalogRequest.current + 1;
    latestCatalogRequest.current = requestNumber;
    setIsCatalogLoading(true);
    setCatalogFailed(false);

    LereService.listHurdleHeatmaps(searchQuery || undefined)
      .then((response) => {
        if (latestCatalogRequest.current === requestNumber) {
          setCatalog(response);
        }
      })
      .catch((error) => {
        if (latestCatalogRequest.current === requestNumber) {
          setCatalog(EMPTY_CATALOG);
          setCatalogFailed(true);
          onLereError(error);
        }
      })
      .finally(() => {
        if (latestCatalogRequest.current === requestNumber) {
          setIsCatalogLoading(false);
        }
      });
  }, [catalogRequestNumber, onLereError, searchQuery]);

  const loadHeatmap = useCallback(
    (offerId: string) => {
      const requestNumber = latestHeatmapRequest.current + 1;
      latestHeatmapRequest.current = requestNumber;
      setSelectedOfferId(offerId);
      setHeatmap(undefined);
      setHeatmapNotFound(false);
      setIsHeatmapLoading(true);

      return LereService.getHurdleHeatmap(offerId)
        .then((response) => {
          if (latestHeatmapRequest.current === requestNumber) {
            setHeatmap(response);
          }
        })
        .catch((error) => {
          if (latestHeatmapRequest.current !== requestNumber) {
            return;
          }

          if (isNotFoundError(error)) {
            setHeatmapNotFound(true);
            return;
          }

          onLereError(error);
        })
        .finally(() => {
          if (latestHeatmapRequest.current === requestNumber) {
            setIsHeatmapLoading(false);
          }
        });
    },
    [onLereError],
  );

  useEffect(() => {
    if (!heatmap) {
      return;
    }

    const expiryTime = new Date(heatmap.expiresAt).getTime();
    if (Number.isNaN(expiryTime)) {
      return;
    }

    const refreshDelay = Math.max(expiryTime - Date.now(), PDF_REFRESH_BUFFER_MS);
    const timeout = window.setTimeout(() => loadHeatmap(heatmap.offerId), refreshDelay);
    return () => window.clearTimeout(timeout);
  }, [heatmap, loadHeatmap]);

  const isSearchPending = searchValue.trim() !== searchQuery;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h2">Hotel sellout hurdle heatmaps</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Find an offer and review its latest generated pricing hurdle heatmap.
        </Typography>
      </Box>

      <TextField
        label="Search offer IDs"
        placeholder="Enter a full or partial offer ID"
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
        sx={{ maxWidth: 520 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment:
            isSearchPending || isCatalogLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} aria-label="Searching heatmaps" />
              </InputAdornment>
            ) : undefined,
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(280px, 340px) minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6">Available heatmaps</Typography>
              {!isCatalogLoading && <Chip size="small" label={catalog.count} />}
            </Stack>
            <IconButton
              size="small"
              aria-label="Refresh heatmap list"
              onClick={() => setCatalogRequestNumber((current) => current + 1)}
              disabled={isCatalogLoading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>

          {catalogFailed && (
            <Alert severity="error" sx={{ m: 2 }}>
              Heatmaps could not be loaded. Try refreshing the list.
            </Alert>
          )}

          {isCatalogLoading && catalog.offerIds.length === 0 ? (
            <Stack spacing={1} sx={{ p: 2 }} aria-label="Loading heatmaps">
              <Skeleton height={42} />
              <Skeleton height={42} />
              <Skeleton height={42} />
            </Stack>
          ) : catalog.offerIds.length > 0 ? (
            <List disablePadding sx={{ maxHeight: { xs: 320, md: 'calc(100vh - 365px)' }, overflowY: 'auto' }}>
              {catalog.offerIds.map((offerId) => (
                <ListItemButton
                  key={offerId}
                  selected={selectedOfferId === offerId}
                  onClick={() => loadHeatmap(offerId)}
                  divider
                >
                  <ListItemIcon sx={{ minWidth: 38 }}>
                    <PictureAsPdfOutlinedIcon color={selectedOfferId === offerId ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText primary={offerId} primaryTypographyProps={{ noWrap: true }} />
                </ListItemButton>
              ))}
            </List>
          ) : !catalogFailed ? (
            <Stack alignItems="center" spacing={1} sx={{ px: 2, py: 6, textAlign: 'center' }}>
              <PictureAsPdfOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
              <Typography variant="body1">No heatmaps found</Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'Try a different offer ID.' : 'Generated heatmaps will appear here.'}
              </Typography>
            </Stack>
          ) : null}
        </Paper>

        <Paper variant="outlined" sx={{ minHeight: 620, overflow: 'hidden' }}>
          {isHeatmapLoading ? (
            <Stack spacing={2} sx={{ p: 2 }} aria-label="Loading selected heatmap">
              <Skeleton width="42%" height={32} />
              <Skeleton width="28%" />
              <Skeleton variant="rectangular" height={520} />
            </Stack>
          ) : heatmap ? (
            <Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Box>
                  <Typography variant="h6">{heatmap.offerId}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Generated {formatDateLongMonthWithMeridiemShort(heatmap.generatedAt)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="text" startIcon={<RefreshIcon />} onClick={() => loadHeatmap(heatmap.offerId)}>
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    component="a"
                    href={heatmap.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open PDF
                  </Button>
                </Stack>
              </Stack>
              <Box
                component="iframe"
                title={`Hurdle heatmap PDF for ${heatmap.offerId}`}
                src={heatmap.pdfUrl}
                sx={{ width: 1, height: { xs: '70vh', md: 'calc(100vh - 365px)' }, minHeight: 540, border: 0 }}
              />
            </Stack>
          ) : heatmapNotFound ? (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 620, p: 4 }}>
              <PictureAsPdfOutlinedIcon color="disabled" sx={{ fontSize: 56 }} />
              <Typography variant="h6">No heatmap found for this offer</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                The heatmap may no longer be available. Refresh the list and choose another offer.
              </Typography>
            </Stack>
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 620, p: 4 }}>
              <PictureAsPdfOutlinedIcon color="disabled" sx={{ fontSize: 56 }} />
              <Typography variant="h6">Select an offer to view its heatmap</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                The latest multi-page PDF will open here.
              </Typography>
            </Stack>
          )}
        </Paper>
      </Box>
    </Stack>
  );
}
